/**
 * Classifiche di tiro, stile ACE.
 *
 * Tre elementi non negoziabili, e nessuno dei tre è estetico:
 *
 *  - **La riga dell'utente è sempre ancorata**, anche fuori dalla top-N. È
 *    quello che fa tornare le persone sulla schermata: chi è quarantesimo e
 *    non si vede apre la classifica una volta sola.
 *  - **I chip di filtro mostrano il conteggio.** Un filtro che darebbe zero
 *    risultati si vede prima di applicarlo, invece di produrre una schermata
 *    vuota che sembra un guasto.
 *  - **Chi non è pubblico legge comunque** (decisione D5), non compare fra le
 *    righe, e vede un invito a esporsi. Una classifica che l'80% degli utenti
 *    non può aprire non genera appartenenza.
 *
 * Spec: docs/shooting/04-frontend-ux.md §4 · 06-decisioni-aperte.md D5
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Target, Trophy } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { fetchAPI } from "../lib/utils";

const PERIODS = [
	{ value: "0", label: "Sempre" },
	{ value: "90", label: "3 mesi" },
	{ value: "365", label: "12 mesi" },
];

const formatScore = (value, metric) => {
	if (value === null || value === undefined) return "—";
	if (metric === "time" || metric === "time_plus")
		return `${value.toFixed(2)}s`;
	if (metric === "points") return String(Math.round(value));
	return value.toFixed(2);
};

const metricLabel = (metric) =>
	({ hit_factor: "HF", time: "Tempo", time_plus: "Tempo", points: "Punti" })[
		metric
	] || "";

export function ShootingLeaderboardsTab() {
	const [drills, setDrills] = useState([]);
	const [drillId, setDrillId] = useState(null);
	const [board, setBoard] = useState(null);
	const [reference, setReference] = useState({ divisions: [], classes: [] });
	const [loading, setLoading] = useState(true);
	const [filters, setFilters] = useState({
		division: "",
		athlete_class: "",
		gender: "",
		optics: "",
		period_days: "0",
	});

	useEffect(() => {
		Promise.all([
			fetchAPI("/shooting/leaderboards").catch(() => []),
			fetchAPI("/shooting/reference").catch(() => ({})),
		]).then(([list, ref]) => {
			const safe = Array.isArray(list) ? list : [];
			setDrills(safe);
			setReference({
				divisions: ref?.divisions || [],
				classes: ref?.classes || [],
			});
			if (safe.length > 0) setDrillId(safe[0].drill_id);
			else setLoading(false);
		});
	}, []);

	const loadBoard = useCallback(async () => {
		if (!drillId) return;
		setLoading(true);
		try {
			const query = new URLSearchParams();
			if (filters.division) query.set("division", filters.division);
			if (filters.athlete_class)
				query.set("athlete_class", filters.athlete_class);
			if (filters.gender) query.set("gender", filters.gender);
			if (filters.optics) query.set("optics", filters.optics);
			if (filters.period_days !== "0")
				query.set("period_days", filters.period_days);
			const suffix = query.toString() ? `?${query.toString()}` : "";
			setBoard(await fetchAPI(`/shooting/leaderboards/${drillId}${suffix}`));
		} catch {
			setBoard(null);
		} finally {
			setLoading(false);
		}
	}, [drillId, filters]);

	useEffect(() => {
		loadBoard();
	}, [loadBoard]);

	const facets = board?.facets || {};
	const metric = board?.metric || "hit_factor";

	// Solo i valori che hanno almeno un risultato: un filtro che darebbe zero
	// non va nemmeno offerto.
	const divisionOptions = useMemo(
		() =>
			reference.divisions.filter((d) => (facets.divisions || {})[d.value] > 0),
		[reference.divisions, facets.divisions],
	);
	const classOptions = useMemo(
		() => reference.classes.filter((c) => (facets.classes || {})[c.value] > 0),
		[reference.classes, facets.classes],
	);

	const setFilter = (key, value) =>
		setFilters((prev) => ({
			...prev,
			[key]: value === "__all__" ? "" : value,
		}));

	const renderRow = (entry, highlight = false) => (
		<div
			key={`${entry.athlete_id}_${entry.position}`}
			className={`flex items-center gap-3 px-3 py-2.5 ${
				highlight ? "bg-volt/10 border border-volt/30 rounded-sm" : ""
			}`}
		>
			<span
				className={`w-7 shrink-0 text-center font-heading text-sm ${
					entry.position === 1 ? "text-volt" : "text-muted-foreground"
				}`}
			>
				{entry.position}
			</span>
			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium truncate">
					{highlight ? "Tu" : entry.athlete_name || "Atleta"}
				</p>
				<p className="text-[11px] text-muted-foreground truncate">
					{[
						entry.shooting?.division,
						entry.shooting?.power_factor === "major" ? "Major" : null,
						entry.shooting?.class_at_time?.toUpperCase(),
						entry.date,
					]
						.filter(Boolean)
						.join(" · ")}
				</p>
			</div>
			<span className="shrink-0 font-heading text-sm text-volt">
				{formatScore(entry.score, metric)}
			</span>
		</div>
	);

	if (drills.length === 0 && !loading) {
		return (
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="flex flex-col items-center justify-center py-12 text-center gap-2">
					<Target className="w-8 h-8 text-muted-foreground" />
					<p className="text-sm text-muted-foreground">
						Nessun risultato di tiro ancora registrato.
					</p>
					<p className="text-xs text-muted-foreground">
						Le classifiche compaiono appena qualcuno registra un punteggio.
					</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<Select value={drillId || ""} onValueChange={setDrillId}>
					<SelectTrigger className="bg-secondary border-transparent rounded-sm h-11">
						<SelectValue placeholder="Scegli un drill" />
					</SelectTrigger>
					<SelectContent className="bg-card border-border">
						{drills.map((drill) => (
							<SelectItem key={drill.drill_id} value={drill.drill_id}>
								{drill.drill_name} · {drill.athletes} atleti
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				<div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
					{[
						{
							key: "division",
							placeholder: "Division",
							options: divisionOptions.map((d) => ({
								value: d.value,
								label: `${d.label} (${facets.divisions[d.value]})`,
							})),
						},
						{
							key: "optics",
							placeholder: "Ottica",
							options: [
								{
									value: "true",
									label: `Con ottica (${facets.optics?.true ?? 0})`,
								},
								{
									value: "false",
									label: `Senza (${facets.optics?.false ?? 0})`,
								},
							],
						},
						{
							key: "athlete_class",
							placeholder: "Classe",
							options: classOptions.map((c) => ({
								value: c.value,
								label: `${c.label} (${facets.classes[c.value]})`,
							})),
						},
						{
							key: "gender",
							placeholder: "Sesso",
							options: [
								{
									value: "male",
									label: `Uomini (${facets.genders?.male ?? 0})`,
								},
								{
									value: "female",
									label: `Donne (${facets.genders?.female ?? 0})`,
								},
							],
						},
					].map((filter) => (
						<Select
							key={filter.key}
							value={filters[filter.key] || "__all__"}
							onValueChange={(v) => setFilter(filter.key, v)}
						>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm h-9 w-auto shrink-0 text-xs gap-1.5">
								<SelectValue placeholder={filter.placeholder} />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="__all__">
									{filter.placeholder}: tutte
								</SelectItem>
								{filter.options.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					))}
					<Select
						value={filters.period_days}
						onValueChange={(v) => setFilter("period_days", v)}
					>
						<SelectTrigger className="bg-secondary border-transparent rounded-sm h-9 w-auto shrink-0 text-xs gap-1.5">
							<SelectValue />
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							{PERIODS.map((p) => (
								<SelectItem key={p.value} value={p.value}>
									{p.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-0">
					{loading ? (
						<div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
						</div>
					) : !board || board.entries.length === 0 ? (
						<div className="flex flex-col items-center gap-2 py-10 text-center px-4">
							<Trophy className="w-7 h-7 text-muted-foreground" />
							<p className="text-sm text-muted-foreground">
								Nessun risultato con questi filtri.
							</p>
						</div>
					) : (
						<>
							<div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
								<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
									{board.total} atleti
								</span>
								<span className="text-[10px] uppercase tracking-wider text-muted-foreground">
									{metricLabel(metric)}
								</span>
							</div>
							<div className="divide-y divide-border/40">
								{board.entries.map((entry) => renderRow(entry))}
							</div>
						</>
					)}

					{/* La propria riga, sempre, anche fuori dalla top-N. */}
					{board?.viewer_entry &&
						!board.entries.some(
							(e) => e.athlete_id === board.viewer_entry.athlete_id,
						) && (
							<div className="p-2 border-t border-border">
								{renderRow(board.viewer_entry, true)}
							</div>
						)}

					{board && !board.viewer_is_public && (
						<div className="px-3 py-2.5 border-t border-border bg-amber-500/5">
							<p className="text-xs text-amber-200/90">
								Il tuo profilo non è pubblico: vedi la classifica ma gli altri
								non vedono te.{" "}
								<Link to="/profile" className="underline font-medium">
									Rendi pubbliche le tue prestazioni
								</Link>
							</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
